import { useEffect, useRef, useState, type FormEvent } from 'react';
import { engine } from '../hooks/useEngine.ts';
import type { ChatEntry } from '../lib/engine.ts';
import { CloseIcon, SendIcon } from './Icons.tsx';

interface Props {
  open: boolean;
  messages: ChatEntry[];
  peerName: string;
  peerTyping: boolean;
  onClose: () => void;
}

export function ChatDock({ open, messages, peerName, peerTyping, onClose }: Props) {
  const [draft, setDraft] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages.length, peerTyping, open]);

  useEffect(() => {
    if (open) {
      engine.markChatRead();
      // Autofocus on desktop only; on mobile it yanks the keyboard over the video.
      if (window.matchMedia('(min-width: 768px)').matches) inputRef.current?.focus();
    }
  }, [open]);

  if (!open) return null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!draft.trim()) return;
    engine.sendChat(draft);
    setDraft('');
  };

  return (
    <aside className="glass pointer-events-auto absolute inset-x-0 bottom-0 z-40 flex h-[65%] flex-col rounded-t-3xl md:inset-y-0 md:left-auto md:right-0 md:h-full md:w-96 md:rounded-t-none md:rounded-l-3xl">
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-white/10 px-4 py-3">
        <h2 className="truncate text-sm font-semibold">Chat with {peerName}</h2>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => engine.next()}
            className="rounded-full bg-phidio-500 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white md:hidden"
          >
            Next
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close chat"
            className="rounded-full p-1.5 text-white/50 transition hover:bg-white/10 hover:text-white"
          >
            <CloseIcon width={18} height={18} />
          </button>
        </div>
      </header>

      <div ref={listRef} className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
        {messages.length === 0 && (
          <p className="pt-6 text-center text-sm text-white/35">
            Say something. "Where are you right now?" works every time.
          </p>
        )}

        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.from === 'me' ? 'justify-end' : 'justify-start'}`}
          >
            <p
              className={`max-w-[85%] break-words rounded-2xl px-3.5 py-2 text-sm ${
                message.from === 'me'
                  ? 'rounded-br-md bg-phidio-500 text-white'
                  : 'rounded-bl-md bg-white/10 text-white/90'
              }`}
            >
              {message.text}
            </p>
          </div>
        ))}

        {peerTyping && (
          <div className="flex justify-start">
            <span className="flex gap-1 rounded-2xl rounded-bl-md bg-white/10 px-3.5 py-3">
              {[0, 1, 2].map((dot) => (
                <span
                  key={dot}
                  className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/60"
                  style={{ animationDelay: `${dot * 0.15}s` }}
                />
              ))}
            </span>
          </div>
        )}
      </div>

      <form
        onSubmit={submit}
        className="flex shrink-0 items-center gap-2 border-t border-white/10 p-3"
        style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
      >
        <input
          ref={inputRef}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            engine.setTyping(event.target.value.length > 0);
          }}
          onBlur={() => engine.setTyping(false)}
          placeholder={`Message ${peerName}…`}
          maxLength={500}
          autoComplete="off"
          className="min-w-0 flex-1 rounded-full bg-white/10 px-4 py-2.5 text-white placeholder:text-white/35 outline-none focus:ring-2 focus:ring-phidio-500/50"
        />
        <button
          type="submit"
          disabled={!draft.trim()}
          aria-label="Send message"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-phidio-500 text-white transition hover:bg-phidio-400 disabled:opacity-30"
        >
          <SendIcon width={20} height={20} />
        </button>
      </form>
    </aside>
  );
}
